import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, Users, Zap, Check, X, AlertTriangle, HelpCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { VerdictBadge } from "./VerdictBadge";
import api from "../lib/api";
import { toast } from "sonner";

const MODEL_ICONS = {
    gpt:    { color: "from-emerald-500/25 to-emerald-500/5", accent: "text-emerald-300", ring: "ring-emerald-500/30", tag: "OpenAI" },
    claude: { color: "from-amber-500/25 to-amber-500/5",    accent: "text-amber-300",    ring: "ring-amber-500/30",   tag: "Anthropic" },
    gemini: { color: "from-sky-500/25 to-sky-500/5",        accent: "text-sky-300",      ring: "ring-sky-500/30",     tag: "Google" },
};

const SPREAD_COPY = {
    unanimous: { label: "All models agree",     tone: "text-emerald-300", icon: Check },
    aligned:   { label: "Models mostly align",  tone: "text-emerald-200", icon: Check },
    mixed:     { label: "Models are mixed",     tone: "text-amber-300",   icon: AlertTriangle },
    divided:   { label: "Models are divided",   tone: "text-red-300",     icon: X },
    no_data:   { label: "No models responded",  tone: "text-muted-foreground", icon: HelpCircle },
};

export function ConsensusPanel({ claim }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [revealed, setRevealed] = useState({});

    const runConsensus = async () => {
        if (!claim || claim.length < 3) {
            toast.error("No claim to check");
            return;
        }
        setLoading(true);
        setData(null);
        setRevealed({});
        try {
            const res = await api.post("/factcheck/consensus", { claim, mode: "claim" });
            setData(res.data);
            // Reveal each model progressively for drama
            const models = res.data?.models || [];
            models.forEach((m, i) => {
                setTimeout(() => setRevealed((prev) => ({ ...prev, [m.model]: true })), 400 + i * 550);
            });
            // Reveal the aggregate last
            setTimeout(() => setRevealed((prev) => ({ ...prev, __aggregate: true })), 400 + models.length * 550 + 400);
        } catch (e) {
            const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || "Consensus check failed";
            toast.error(typeof msg === "string" ? msg : "Consensus check failed");
        } finally {
            setLoading(false);
        }
    };

    if (!data && !loading) {
        return (
            <Card className="p-5 sm:p-6 border-white/10 bg-gradient-to-br from-primary/5 via-card/70 to-accent/5" data-testid="consensus-panel-cta">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                    <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-full bg-primary/15 text-primary shrink-0">
                            <Users className="h-5 w-5" strokeWidth={2.5} />
                        </div>
                        <div>
                            <h3 className="font-display font-bold text-accent text-lg tracking-tight">Multi-Model Consensus</h3>
                            <p className="text-sm text-muted-foreground mt-1 max-w-md">
                                Run this claim through <span className="text-emerald-300">GPT-5.2</span>, <span className="text-amber-300">Claude Sonnet 4.5</span> and <span className="text-sky-300">Gemini 3 Flash</span> in parallel and see how they agree.
                            </p>
                        </div>
                    </div>
                    <Button
                        onClick={runConsensus}
                        className="rounded-full whitespace-nowrap sm:self-center"
                        data-testid="run-consensus-btn"
                    >
                        <Sparkles className="h-4 w-4 mr-2" />
                        Run Consensus
                    </Button>
                </div>
            </Card>
        );
    }

    return (
        <Card className="p-5 sm:p-6 border-white/10 bg-gradient-to-br from-primary/5 via-card/70 to-accent/5" data-testid="consensus-panel">
            <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-full bg-primary/15 text-primary">
                    <Users className="h-4 w-4" strokeWidth={2.5} />
                </div>
                <h3 className="font-display font-bold text-accent text-lg tracking-tight">Multi-Model Consensus</h3>
                {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground ml-auto" />}
            </div>

            {loading && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {["gpt", "claude", "gemini"].map((k) => (
                        <div key={k} className={`h-32 rounded-2xl bg-gradient-to-br ${MODEL_ICONS[k].color} border border-white/5 flex items-center justify-center animate-pulse`}>
                            <Loader2 className={`h-5 w-5 animate-spin ${MODEL_ICONS[k].accent}`} />
                        </div>
                    ))}
                </div>
            )}

            {data && (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {data.models.map((m) => (
                            <ModelCard key={m.model} model={m} revealed={!!revealed[m.model]} />
                        ))}
                    </div>

                    <AnimatePresence>
                        {revealed.__aggregate && data.aggregate && (
                            <motion.div
                                initial={{ opacity: 0, y: 12, scale: 0.97 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                className="mt-5"
                                data-testid="consensus-aggregate"
                            >
                                <AggregateBanner agg={data.aggregate} />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </>
            )}
        </Card>
    );
}

function ModelCard({ model, revealed }) {
    const cfg = MODEL_ICONS[model.model] || MODEL_ICONS.gpt;
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: revealed ? 1 : 0.3, y: revealed ? 0 : 6 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className={`rounded-2xl p-4 bg-gradient-to-br ${cfg.color} ring-1 ${cfg.ring} border border-white/5 min-h-[152px]`}
            data-testid={`consensus-model-${model.model}`}
        >
            <div className="flex items-center justify-between mb-2">
                <div>
                    <div className={`text-[11px] uppercase tracking-widest ${cfg.accent} font-bold`}>{cfg.tag}</div>
                    <div className="font-display font-bold text-accent text-sm mt-0.5">{model.label}</div>
                </div>
                {model.ok ? <Zap className={`h-4 w-4 ${cfg.accent}`} /> : <X className="h-4 w-4 text-red-400" />}
            </div>
            {model.ok && revealed ? (
                <>
                    <div className="mt-1">
                        <VerdictBadge verdict={model.verdict} confidence={model.confidence} size="sm" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2.5 leading-relaxed line-clamp-4">
                        {model.reasoning || "No reasoning provided."}
                    </p>
                </>
            ) : model.ok ? (
                <div className="h-12 rounded-lg bg-white/5 animate-pulse mt-2" />
            ) : (
                <p className="text-xs text-red-300 mt-3">Model did not respond in time.</p>
            )}
        </motion.div>
    );
}

function AggregateBanner({ agg }) {
    const spread = SPREAD_COPY[agg.spread] || SPREAD_COPY.no_data;
    const Icon = spread.icon;
    return (
        <div className="rounded-2xl p-5 border border-primary/30 bg-primary/5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                <div className="flex items-center gap-4">
                    <div className="relative">
                        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                            <circle cx="18" cy="18" r="15.9" fill="none" stroke="hsl(var(--border))" strokeWidth="2.5" />
                            <circle
                                cx="18" cy="18" r="15.9" fill="none"
                                stroke="hsl(var(--primary))"
                                strokeWidth="2.5"
                                strokeDasharray={`${agg.agreement_pct}, 100`}
                                strokeLinecap="round"
                            />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center font-display font-bold text-accent text-lg">
                            {agg.agreement_pct}%
                        </div>
                    </div>
                    <div>
                        <div className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${spread.tone}`}>
                            <Icon className="h-3.5 w-3.5" />
                            {spread.label}
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Consensus:</span>
                            <VerdictBadge verdict={agg.consensus_verdict} confidence={agg.avg_confidence} size="sm" />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1.5">
                            {agg.responded} of 3 models responded · avg confidence {agg.avg_confidence}%
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ConsensusPanel;
